import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PreviewModal = ({ isOpen, onClose, resource }) => {
  if (!isOpen || !resource) return null;

  const getFileIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return 'FileText';
      case 'doc': case 'docx':
        return 'FileText';
      case 'xls': case 'xlsx':
        return 'Sheet';
      case 'ppt': case 'pptx':
        return 'Presentation';
      case 'zip':
        return 'Archive';
      case 'mp4':
        return 'Video';
      case 'mp3':
        return 'Music';
      default:
        return 'File';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={getFileIcon(resource?.type)} size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{resource?.title}</h2>
              <p className="text-sm text-muted-foreground">{resource?.description}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Resource Info */}
          <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <span className="flex items-center space-x-1">
                <Icon name="Calendar" size={14} />
                <span>Updated {formatDate(resource?.updatedAt)}</span>
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                resource?.category === 'Forms' ? 'bg-blue-100 text-blue-800' :
                resource?.category === 'Policies' ? 'bg-green-100 text-green-800' :
                resource?.category === 'Tenders' ? 'bg-orange-100 text-orange-800' :
                resource?.category === 'Academic' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {resource?.category}
              </span>
              {resource?.isNew && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-accent/20 text-accent">
                  New
                </span>
              )}
            </div>
          </div>

          {/* Preview Content */}
          <div className="bg-muted/30 rounded-lg border border-border overflow-hidden">
            {resource?.category?.toLowerCase() === 'policies' ? (
              // Preview available for policies
              resource?.type?.toLowerCase() === 'pdf' ? (
                <div className="w-full h-96">
                  <iframe
                    src={resource?.fileUrl}
                    className="w-full h-full border-0"
                    title={`Preview of ${resource?.title}`}
                    onError={(e) => {
                      console.error('PDF preview failed:', e);
                      // Fallback to download if iframe fails
                      e.target.style.display = 'none';
                      const fallbackDiv = e.target.parentElement.querySelector('.pdf-fallback');
                      if (fallbackDiv) fallbackDiv.style.display = 'flex';
                    }}
                  />
                </div>
              ) : ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(resource?.type?.toLowerCase()) ? (
                <div className="w-full h-96">
                  <iframe
                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(resource?.fileUrl)}&embedded=true`}
                    className="w-full h-full border-0"
                    title={`Preview of ${resource?.title}`}
                    onError={(e) => {
                      console.error('Document preview failed:', e);
                      // Fallback to download if iframe fails
                      e.target.style.display = 'none';
                      const fallbackDiv = e.target.parentElement.querySelector('.doc-fallback');
                      if (fallbackDiv) fallbackDiv.style.display = 'flex';
                    }}
                  />
                </div>
              ) : resource?.type?.toLowerCase() === 'mp4' ? (
                <video
                  src={resource?.fileUrl}
                  controls
                  className="w-full h-96"
                  title={`Preview of ${resource?.title}`}
                >
                  Your browser does not support the video tag.
                </video>
              ) : resource?.type?.toLowerCase() === 'mp3' ? (
                <div className="flex flex-col items-center justify-center h-96 text-center p-8">
                  <Icon name="Music" size={64} className="text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-4">Audio Preview</h3>
                  <audio
                    src={resource?.fileUrl}
                    controls
                    className="w-full max-w-md"
                    title={`Preview of ${resource?.title}`}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              ) : resource?.type?.toLowerCase() === 'image' ? (
                <div className="flex items-center justify-center h-96 p-4">
                  <img
                    src={resource?.fileUrl}
                    alt={resource?.title}
                    className="max-w-full max-h-full object-contain"
                    title={`Preview of ${resource?.title}`}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-96 text-center p-8">
                  <Icon name={getFileIcon(resource?.type)} size={64} className="text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Preview Not Available</h3>
                  <p className="text-muted-foreground mb-4">
                    This file type cannot be previewed in the browser. Please download the file to view its contents.
                  </p>
                  <Button
                    variant="default"
                    iconName="Download"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = resource.fileUrl;
                      link.download = resource.title + '.' + resource.type;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    Download File
                  </Button>
                </div>
              )
            ) : (
              // Download only for non-policies
              <div className="flex flex-col items-center justify-center h-96 text-center p-8">
                <Icon name={getFileIcon(resource?.type)} size={64} className="text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Download Required</h3>
                <p className="text-muted-foreground mb-4">
                  This document is available for download only. Click the download button below to access the file.
                </p>
                <Button
                  variant="default"
                  iconName="Download"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = resource.fileUrl;
                    link.download = resource.title + '.' + resource.type;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download {resource?.type?.toUpperCase()} File
                </Button>
              </div>
            )}
            {/* PDF fallback */}
            {resource?.type?.toLowerCase() === 'pdf' && (
              <div className="pdf-fallback flex flex-col items-center justify-center h-96 text-center p-8" style={{ display: 'none' }}>
                <Icon name="FileText" size={64} className="text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">PDF Preview Unavailable</h3>
                <p className="text-muted-foreground mb-4">
                  Unable to preview PDF in browser. Please download the file to view its contents.
                </p>
                <Button
                  variant="default"
                  iconName="Download"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = resource.fileUrl;
                    link.download = resource.title + '.' + resource.type;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download PDF
                </Button>
              </div>
            )}
            {/* Document fallback */}
            {['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(resource?.type?.toLowerCase()) && (
              <div className="doc-fallback flex flex-col items-center justify-center h-96 text-center p-8" style={{ display: 'none' }}>
                <Icon name={getFileIcon(resource?.type)} size={64} className="text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Document Preview Unavailable</h3>
                <p className="text-muted-foreground mb-4">
                  Unable to preview document in browser. Please download the file to view its contents.
                </p>
                <Button
                  variant="default"
                  iconName="Download"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = resource.fileUrl;
                    link.download = resource.title + '.' + resource.type;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download Document
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {resource?.category?.toLowerCase() !== 'policies' && (
            <Button
              variant="default"
              iconName="Download"
              onClick={() => {
                const link = document.createElement('a');
                link.href = resource.fileUrl;
                link.download = resource.title + '.' + resource.type;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
